import { watch, Ref, ref, reactive, WatchStopHandle } from 'vue';
import emitter, { EB_EVENT_NAME } from '@/utils/eventbus';
import { IComboData } from '@/service/broadcast/types';
import { useStore } from 'vuex';
import { loadImage } from '@/utils';

enum IComboType {
  all = 0,
  guest = 1,
  host = 2,
}

export interface ComboQueueEle {
  // control state
  comboId: string, // combo_id 连击id
  comboType: IComboType, // 归属
  value: number, // star * number 价值
  startTime: number, // startTime 进队时间
  expiredTime: number, // expiredTime 过期时间
  isShowed: boolean,
  isImageLoaded: boolean, // 图片是否已加载
  // raw: IComboData, // data 连击数据
  // display data
  treasureId: number,
  treasureName: string,
  treasureIcon: string,
  treasureNumber: number,
  authorAvatar: string
  authorName: string,
}

export interface ComboContext {
  combos: ComboQueueEle[],
}


// 重排序延迟, 和入场动画时间尽量保持一致
const sortDelay: number = 100;
// 检查时间片
const minTimeSlice: number = 300; // 动画毫秒数，和control的动画时间尽量保持一致
// 最小连击展示时间
// const minDisplayTime: number = 1000;  // 1000ms
// 连击动画最大容量
const maxRenderCapacity: number = 2;


// const comboMap: Map<string, IComboData> = new Map<string, IComboData>();
const bufferCombos: ComboQueueEle[] = reactive(new Array<ComboQueueEle>(0));
// const guestCombos: ComboQueueEle[] = new Array<ComboQueueEle>(0);
const renderCombos: ComboQueueEle[] = reactive(new Array<ComboQueueEle>(0));


const mid: Ref<number> = ref(0);


const resolveCombo = async (params: { data: IComboData[] }): Promise<void> => {
  // 不是函数式编程，后续可以优化
  for (let i = 0; i < params.data.length; i++) {
    const combo: IComboData = params.data[i];
    try {
      insertCombo(
        bufferCombos,
        combo,
        mid.value === combo?.author?.mid ? IComboType.host : IComboType.guest,
      );
    } catch (e) {
      continue;
    }
  }
  sortCombos(bufferCombos);
  adjuestRenderCombo();

  if (intervalChecker) {
    clearTimeout(intervalChecker);
  }
  intervalChecker = check(sortDelay);
};

emitter.on(EB_EVENT_NAME.COMBO_RECEIVED, resolveCombo);

// 周期检查，删除无用数据，将可用的数据塞入render
let intervalChecker: NodeJS.Timeout | null = null;
const check = (delayTime: number = minTimeSlice) => {
  return setTimeout(() => {
    if (!bufferCombos.length && !renderCombos.length) {
      intervalChecker = null;
      return;
    }
    // 清洗连击数据
    const now = Date.now();
    washCombos(bufferCombos, now, true);
    washCombos(renderCombos, now);

    // 调用连击数据
    adjuestRenderCombo();
    // delaySortRenderCombos();

    const renderEleLength = renderCombos.length;
    if (renderEleLength > 1) {
      sortCombos(renderCombos);
    }

    if (bufferCombos.length || renderEleLength) {
      intervalChecker = check();
    } else {
      intervalChecker = null;
    }
  }, delayTime);
};


// 插入排序位置，并删除上一条同combo-id记录
const insertCombo = (queue: ComboQueueEle[], combo: IComboData, type: IComboType) => {
  const index: number = queue.findIndex((ele: ComboQueueEle) => ele.comboId === combo.combo_id);
  const { author, treasure, style } = combo;
  const value: number = treasure.stars * treasure.number;
  const isNew: boolean = index === -1;
  const startTime: number = Date.now();
  const expiredTime: number = startTime + style.duration * 1000;

  if (isNew) {
    const newQueueEle = {
      // control state
      comboId: combo.combo_id,
      comboType: type,
      value,
      startTime,
      expiredTime,
      isShowed: false,
      isImageLoaded: false,
      // display data
      treasureId: treasure.id,
      treasureName: treasure.title,
      treasureIcon: treasure.icon?.replace(/^https?:/, ''),
      treasureNumber: treasure.number,
      authorAvatar: `${author.avatar?.replace(/^https?:/, '')}@64w_64h_1e_1c_1f.webp`,
      authorName: author.name,
    } as ComboQueueEle;

    if (!newQueueEle.isImageLoaded) {
      loadImage(newQueueEle.treasureIcon);
      loadImage(newQueueEle.authorAvatar);
      newQueueEle.isImageLoaded = true;
    }
    queue.push(newQueueEle);
  } else if (value > queue[index].value) {
    queue[index].value = value;
    queue[index].startTime = startTime;
    queue[index].expiredTime = expiredTime;
    queue[index].treasureNumber = treasure.number;
    queue[index].isShowed = false;
  }
};

// 队列排序，1-价值，2-时间
const sortCombos = (queue: ComboQueueEle[]): void => {
  queue.sort((a, b) => {
    //比对类型
    const typeCompareResult: number = b.comboType - a.comboType;
    if (typeCompareResult !== 0) {
      return typeCompareResult;
    }
    // 倒序价值
    const valueCompareResult: number = b.value - a.value;
    if (valueCompareResult !== 0) {
      return valueCompareResult;
    }
    // 顺序开始时间
    return a.startTime - b.startTime;
  });
};


// 删除过期数据
const washCombos = (queue: ComboQueueEle[], now: number, isDeepWash: boolean = false): void => {
  for (let i = queue.length - 1; i >= 0; i--) {
    const { isShowed, expiredTime, comboId } = queue[i] || {};
    const isExpired = expiredTime <= now;

    if (isDeepWash) {
      if (isExpired || (i >= maxRenderCapacity && isShowed) || !comboId) {
        queue.splice(i, 1);
      }
    } else {
      if (isExpired) {
        // const elementToResort = queue[i];
        // elementToResort.comboId = '';
        queue.splice(i, 1);
        // queue.push(elementToResort);
      }
    }
  }
};


// // 获取可用数据
const adjuestRenderCombo = () => {
  // 需要处理竞争关系
  if (!bufferCombos.length) {
    return;
  }

  const positions: boolean[] = new Array<boolean>(maxRenderCapacity).fill(false);

  for (let i = 0; i < maxRenderCapacity; i++) {
    const combo: ComboQueueEle | undefined = bufferCombos[i];

    if (!combo) {
      continue;
    }
    // renderCombos[i] = combo;
    let index = positions.indexOf(false);

    const existedComboIndex = renderCombos.findIndex((c: ComboQueueEle | undefined) => c?.comboId === combo.comboId);
    if (existedComboIndex !== -1) {
      // 如果原来已经有了对应combo，则直接使用同comboId位置
      index = existedComboIndex;
    } else {
      const emptyComboIndex = renderCombos.findIndex((c: ComboQueueEle | undefined) => !c?.comboId);
      // 如果存在空id
      // 如果就是要插入的位置
      if (emptyComboIndex === i) {
        index = emptyComboIndex;
      } else if (
        emptyComboIndex > i
        && combo.comboType === IComboType.host
        && renderCombos.some(c => (c?.comboType === IComboType.guest))
      ) {
        // 如果插入位置在后面，且有客态存在，则将空态调整到客态之前
        // const temp = renderCombos[emptyComboIndex];
        renderCombos.splice(emptyComboIndex, 1);
        // renderCombos.splice(i, 0, temp);
      }
    }
    positions[index] = true;
    renderCombos[index] = combo;
    bufferCombos[i].isShowed = true;
  }
};


export default function useCombo(): ComboContext {
  const store = useStore();

  const userInfo = store.state?.global?.userInfo;
  if (userInfo) {
    mid.value = parseInt(userInfo.mid, 10);
  } else {
    const stopWatchMid: WatchStopHandle = watch(store.state.global, (newState: any) => {
      if (newState.userInfo) {
        mid.value = parseInt(newState.userInfo?.mid, 10);
        stopWatchMid();
      }
    });
  }

  return {
    combos: renderCombos,
  };
};
